import React, { useState, Fragment } from "react";
import { motion } from "framer-motion";
import { Listbox, Transition } from "@headlessui/react";
import { ChevronUp, Check } from "lucide-react";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:4000";

function ThemedSelect({ label, options, value, onChange }) {
  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-white/80">{label}</label>
      <Listbox value={value} onChange={onChange}>
        <div className="relative">
          <Listbox.Button className="relative w-full cursor-default rounded-xl bg-white/5 border border-white/15 py-3 pl-4 pr-10 text-left text-white focus:outline-none focus:ring-2 focus:ring-[#4DB2D3]/70">
            <span className="block truncate">{value || "Select"}</span>
            <span className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
              <ChevronUp className="h-5 w-5 text-white/60" />
            </span>
          </Listbox.Button>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-150"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <Listbox.Options className="absolute mt-2 max-h-60 w-full overflow-auto rounded-xl bg-[#0A0A0A] border border-white/10 py-1 shadow-xl focus:outline-none z-20">
              {options.map((opt, idx) => (
                <Listbox.Option
                  key={idx}
                  value={opt}
                  className={({ active }) =>
                    `relative cursor-default select-none py-2 pl-10 pr-4 ${
                      active ? "bg-[#4DB2D3]/30 text-white" : "text-white/90"
                    }`
                  }
                >
                  {({ selected }) => (
                    <>
                      <span
                        className={`block truncate ${
                          selected ? "font-medium" : "font-normal"
                        }`}
                      >
                        {opt}
                      </span>
                      {selected && (
                        <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-[#4DB2D3]">
                          <Check className="h-5 w-5" />
                        </span>
                      )}
                    </>
                  )}
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </Transition>
        </div>
      </Listbox>
    </div>
  );
}

export default function Contact() {
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [msg, setMsg] = useState("");
  const [category, setCategory] = useState("");
  const [subType, setSubType] = useState("");
  const [amount, setAmount] = useState("");
  const [ok, setOk] = useState(false);
  const [err, setErr] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [hp, setHp] = useState("");

  const subOptions = {
    Allocator: [
      "Family Office / Private Fund",
      "Wealth Manager",
      "Fund Distributor",
      "Other",
    ],
    Investor: [
      "Angel Investor",
      "HNWI (Crypto-native)",
      "Private Individual",
      "Institutional Investor",
    ],
    Partnership: [
      "Strategy Provider",
      "Custody Partner",
      "Regulatory / Legal Partner",
      "Media / Affiliate Collaboration",
    ],
  };

  const amountOptions = {
    Allocator: ["$500k - $1M", "$1M – $3M", "$3M – $5M", "$5M+", "Custom"],
    Investor: ["$25K – $100K", "$100K – $500K", "$500K+"],
    Partnership: ["N/A"],
  };

  function validEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setErr("");
    setOk(false);

    if (hp) return;
    if (!validEmail(email)) {
      setErr("Please provide a valid email.");
      return;
    }
    if (!category) {
      setErr("Please select your category.");
      return;
    }
    if (!subType) {
      setErr("Please specify your role or type.");
      return;
    }
    if (category !== "Partnership" && !amount) {
      setErr("Please select an estimated allocation amount.");
      return;
    }
    if (msg.trim().length < 10) {
      setErr("Please add a short message (at least 10 characters).");
      return;
    }

    setSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/send-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: subType,
          email,
          phone,
          message: `
Category: ${category}
Type: ${subType}
Estimated Amount: ${amount}
Phone: ${phone}
Message: ${msg}
          `,
        }),
      });

      if (!response.ok) throw new Error("Server error");
      const data = await response.json();

      if (data.success) {
        setOk(true);
        setEmail("");
        setPhone("");
        setMsg("");
        setCategory("");
        setSubType("");
        setAmount("");
      } else {
        throw new Error(data.error || "Failed to send email");
      }
    } catch (error) {
      console.error(error);
      setErr("Something went wrong. Please try again later.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section
      id="contact"
      className="relative py-28 bg-gradient-to-b from-black via-[#0A0A0A] to-[#0A2A66] text-white overflow-hidden"
    >
      {/* Background lighting */}
      <div className="pointer-events-none absolute inset-0 opacity-20">
        <div className="absolute top-1/3 left-1/3 w-[600px] h-[600px] bg-[radial-gradient(circle,rgba(77,178,211,0.25),transparent_70%)] blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-2xl mx-auto px-6">
        <motion.div
          className="text-center mb-8"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="inline-block text-[11px] tracking-[0.2em] font-semibold uppercase text-[#A5F5E2]/90 bg-white/10 px-4 py-1 rounded-full border border-[#4DB2D3]/30 backdrop-blur-sm">
            Contact
          </span>
          <h2 className="text-4xl md:text-5xl font-bold mt-5 leading-tight">
            Request Access or Partnership Brief
          </h2>
          <p className="text-white/70 mt-3">
            Average response time: within 24 hours.
          </p>
        </motion.div>

        <motion.form
          onSubmit={handleSubmit}
          className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8 backdrop-blur-md shadow-xl space-y-6"
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          {/* Honeypot */}
          <div className="hidden">
            <input type="text" value={hp} onChange={(e) => setHp(e.target.value)} />
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">
              Contact Email
            </label>
            <input
              type="email"
              inputMode="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-xl bg-white/5 border border-white/15 text-white placeholder-white/50 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#4DB2D3]/60"
              placeholder="your@email.com"
            />
          </div>

          {/* Phone */}
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">
              Phone Number
            </label>
            <input
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="w-full rounded-xl bg-white/5 border border-white/15 text-white placeholder-white/50 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#4DB2D3]/60"
              placeholder="+1 234 567 890"
            />
          </div>

          {/* Category selects */}
          <ThemedSelect
            label="Inquiry Type"
            options={["Allocator", "Investor", "Partnership"]}
            value={category}
            onChange={(val) => {
              setCategory(val);
              setSubType("");
              setAmount("");
            }}
          />

          {category && (
            <>
              <ThemedSelect
                label={`${category} Category`}
                options={subOptions[category]}
                value={subType}
                onChange={setSubType}
              />

              {category !== "Partnership" && (
                <ThemedSelect
                  label="Estimated Allocation / Commitment"
                  options={amountOptions[category]}
                  value={amount}
                  onChange={setAmount}
                />
              )}
            </>
          )}

          {/* Message */}
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">
              Brief (objective, timing, notes)
            </label>
            <textarea
              rows={5}
              required
              value={msg}
              onChange={(e) => setMsg(e.target.value)}
              className="w-full rounded-xl bg-white/5 border border-white/15 text-white placeholder-white/50 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#4DB2D3]/60"
              placeholder="e.g., allocation interest, upcoming fund, or strategic collaboration."
            />
          </div>

          {/* Error / Success */}
          {err && (
            <p className="text-sm text-red-300 bg-red-900/20 border border-red-400/20 rounded-lg px-3 py-2">
              {err}
            </p>
          )}
          {ok && (
            <p className="text-sm text-emerald-300 bg-emerald-900/20 border border-emerald-400/20 rounded-lg px-3 py-2">
              Thanks — your inquiry has been received. We'll respond within 24h.
            </p>
          )}

          {/* Submit */}
          <div className="pt-2">
            <button
              type="submit"
              disabled={submitting}
              className="relative inline-flex items-center justify-center w-full md:w-auto px-7 py-3.5 rounded-xl font-semibold bg-gradient-to-r from-[#4DB2D3] to-[#3D8FAA] hover:from-[#6CD6E6] hover:to-[#4DB2D3] text-black transition disabled:opacity-60"
            >
              {submitting ? "Sending…" : "Send Request"}
            </button>
          </div>

          <p className="text-xs text-white/50">
            By submitting, you consent to receive one reply to your inquiry. No mailing lists. No
            third-party sharing.
          </p>
        </motion.form>
      </div>
    </section>
  );
}
